import { Suspense, useMemo } from 'react'
import { useOmniAuth, useOmniSession } from '@omni/auth'
import { useToast } from '@omni/ui'
import { ShellProvider, type OmniShellContext } from '@omni/shell-context'
import type { Surface } from '../surfaces'
import { workspace } from '../lib/config'
import { shellEvents } from '../lib/events'
import { shellNavigate } from '../lib/navigation'
import { useShellStore } from '../lib/store'
import { Splash } from '../components/Splash'

/**
 * Mounts one surface under a fresh ShellProvider carrying that surface's
 * basePath (projects reads it for its router basename; the other surfaces
 * only branch on isStandalone). Only the active surface is mounted — each
 * one owns its own <BrowserRouter> and routers cannot coexist on one
 * history, so switching surfaces unmounts the old one.
 */
export function SurfaceHost({ surface }: { surface: Surface }) {
  const { signOut } = useOmniAuth()
  const session = useOmniSession()
  const { toast } = useToast()
  const openContextCard = useShellStore((s) => s.openContextCard)
  const closeContextCard = useShellStore((s) => s.closeContextCard)

  const value = useMemo<OmniShellContext>(
    () => ({
      user: {
        id: session.primaryUser?.id ?? 'unknown',
        email: session.primaryEmail ?? '',
      },
      theme: 'light',
      workspace,
      events: shellEvents,
      navigate: shellNavigate,
      openContextCard,
      closeContextCard,
      toast: (msg, opts) => {
        toast(msg, opts)
      },
      signOut,
      isStandalone: false,
      basePath: surface.meta.basePath,
    }),
    [
      session.primaryUser?.id,
      session.primaryEmail,
      openContextCard,
      closeContextCard,
      toast,
      signOut,
      surface.meta.basePath,
    ],
  )

  const SurfaceApp = surface.App
  // Unreachable for enabled surfaces (every enabled id has an App in the
  // per-build surface-apps map); only disabled metas carry App: undefined,
  // and those render DisabledModule, never SurfaceHost.
  if (!SurfaceApp) return <Splash label={`Loading ${surface.meta.label}…`} />
  return (
    <ShellProvider value={value}>
      <Suspense fallback={<Splash label={`Loading ${surface.meta.label}…`} />}>
        <SurfaceApp />
      </Suspense>
    </ShellProvider>
  )
}
